import {
  SearchIcon,
  Select,
  TextInput,
  Modal,
  RadioButton,
} from '@medeloop/medeloop-components-library';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOrganizationById } from 'src/api/organizations';
import { selectAuthToken } from 'src/store/selectors';
import {
  Organization,
  OrganizationDataSource,
  OrganizationResearcher,
} from 'src/types/organizations';
import { Study, UserStudyRole } from 'src/types/studies';
import { addUserToDataSource, addUserToStudy } from 'src/api/users';
import { showToast } from 'src/store/toast';
import { UserDataSourceRole } from 'src/types/data-sources';
import { useOrganizationDetailsContext } from './organization-details';
import { useTranslation } from 'react-i18next';

export const AssignResearcherPage = () => {
  const token = useSelector(selectAuthToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { container } = useOrganizationDetailsContext();

  const { organizationId } = useParams();
  const [filterOrgId, setFilterOrgId] = useState(organizationId);

  const [params] = useSearchParams();
  const userId = params.get('userId');

  if (!organizationId || !userId) {
    navigate(-1);
    return;
  }

  const [search, setSearch] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<string | undefined>(undefined);
  const [selectedEntityType, setSelectedEntityType] = useState<'study' | 'data_source'>('study'); // appending _lower to make translations easier

  const [toConfirm, setToConfirm] = useState(false);

  const { isError, data } = useQuery<{
    organization: Organization;
    studies: Study[];
    researchers: OrganizationResearcher[];
    admins: OrganizationResearcher[];
    dataSources: OrganizationDataSource[];
    organizationalUnits: Organization[];
  }>({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (organizationId) {
        const result = await getOrganizationById({ token, id: organizationId });
        return result;
      }
    },
  });

  const { data: selectedOrgData } = useQuery<{
    organization: Organization;
    studies: Study[];
    researchers: OrganizationResearcher[];
    admins: OrganizationResearcher[];
    dataSources: OrganizationDataSource[];
    organizationalUnits: Organization[];
  }>({
    queryKey: ['organization', filterOrgId],
    queryFn: async () => {
      if (filterOrgId) {
        const result = await getOrganizationById({ token, id: filterOrgId });
        return result;
      }
    },
  });

  const addUserToStudyMutation = useMutation({
    mutationFn: () => {
      if (selectedEntity) {
        if (selectedEntityType === 'study') {
          return addUserToStudy({
            token,
            userId,
            studyId: selectedEntity,
            role: UserStudyRole.RESEARCHER,
          });
        } else if (selectedEntityType === 'data_source') {
          return addUserToDataSource({
            token,
            userId,
            dataSourceId: selectedEntity,
            role: UserDataSourceRole.RESEARCHER,
          });
        }
      }

      return Promise.reject();
    },
    onSuccess: () => {
      dispatch(
        showToast({
          message: t('user_assigned_to_entity', {
            entityName: t(selectedEntityType).toLowerCase(),
          }),
          type: 'success',
        }),
      );
      navigate(-1);
    },
  });

  const organizationOptions = data
    ? [
        {
          label: t('organization_header'),
          options: [
            {
              label: data.organization.displayName,
              value: data.organization.organizationId,
            },
          ],
        },
        {
          label: t('organizational_units_header'),
          options: data.organizationalUnits?.map((ou) => ({
            label: ou.displayName,
            value: ou.organizationId,
          })),
        },
      ]
    : [];

  const filteredStudies = selectedOrgData?.studies?.filter((study) => {
    const searchTerm = `${study.name} ${study.displayName} ${study.studyId}`.toLowerCase();
    return searchTerm.includes(search.toLowerCase());
  });

  const filteredDataSources = selectedOrgData?.dataSources?.filter((dataSource) => {
    const searchTerm =
      `${dataSource.dataSourceName} ${dataSource.dataSourceId} ${dataSource.dataSourceDisplayName}`.toLowerCase();
    return searchTerm.includes(search.toLowerCase());
  });

  return (
    <Modal
      isOpen
      title={t('assign_researcher')}
      disabled={isError || !selectedEntity}
      container={container}
      actionType="primary"
      actionText={t('save')}
      actionFunction={() => {
        if (toConfirm) {
          setToConfirm(false);
          addUserToStudyMutation.mutate();
        } else {
          setToConfirm(true);
        }
      }}
      onCancel={() => navigate(-1)}
    >
      {toConfirm ? (
        <p className="w-[512px]">
          {t('assign_researcher_confirmation', {
            selectedEntityType: t(selectedEntityType).toLowerCase(),
          })}
        </p>
      ) : (
        <>
          {data && (
            <>
              <div className="flex flex-row gap-4 items-center w-full">
                <div className="flex-grow">
                  <TextInput
                    preChildren={<SearchIcon className="w-4 text-gray-500" />}
                    placeholder={t('find_entities')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Select
                  options={organizationOptions}
                  value={filterOrgId}
                  onValueChange={(orgId) => setFilterOrgId(orgId)}
                  className="!w-60"
                />
              </div>

              {selectedOrgData && (
                <div className="flex flex-col gap-4 mt-4 overflow-auto max-h-72 min-w-[512px]">
                  <>
                    <h2 className="font-bold">{t('studies_header')}</h2>
                    {filteredStudies?.length ? (
                      filteredStudies?.map((study) => (
                        <RadioButton
                          label={study.name}
                          value={study.studyId}
                          checked={selectedEntity === study.studyId}
                          onChange={() => {
                            setSelectedEntity(study.studyId);
                            setSelectedEntityType('study');
                          }}
                        />
                      ))
                    ) : (
                      <p className="text-gray-400">{t('no_studies_found')}</p>
                    )}

                    <h2 className="font-bold">{t('data_sources_header')}</h2>
                    {filteredDataSources?.length ? (
                      filteredDataSources?.map((dataSource) => (
                        <RadioButton
                          label={dataSource.dataSourceName}
                          value={dataSource.dataSourceId}
                          checked={selectedEntity === dataSource.dataSourceId}
                          onChange={() => {
                            setSelectedEntity(dataSource.dataSourceId);
                            setSelectedEntityType('data_source');
                          }}
                        />
                      ))
                    ) : (
                      <p className="text-gray-400">{t('no_data_sources_found')}</p>
                    )}
                  </>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};
